import { useEffect } from "react";
import { Route, Switch, Redirect } from "wouter";
import { useAuth } from "./contexts/AuthContext";
import { useLanguage } from "./contexts/LanguageContext";
import { AppShell } from "./components/layout/AppShell";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import AccountSettings from "./pages/account/AccountSettings";
import ForcePasswordChange from "./pages/account/ForcePasswordChange";
import Users from "./pages/admin/Users";
import Drivers from "./pages/fleet/Drivers";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
import { Placeholder } from "./pages/Placeholder";
import OrdersList from "./pages/OrdersList";
import OrderDetail from "./pages/OrderDetail";
import NewOrder from "./pages/agency/NewOrder";
import NewOrderForAgency from "./pages/dispatcher/NewOrderForAgency";
import Parties from "./pages/Parties";
import Notifications from "./pages/Notifications";
import PricingRules from "./pages/PricingRules";
import FleetCosts from "./pages/FleetCosts";
import VehicleModels from "./pages/admin/VehicleModels";
import Pois from "./pages/admin/Pois";
import RoutePrices from "./pages/admin/RoutePrices";
import CharterPlans from "./pages/admin/CharterPlans";
import ExchangeRates from "./pages/admin/ExchangeRates";
import CommissionRules from "./pages/admin/CommissionRules";
import SettlementConsole from "./pages/settlements/SettlementConsole";
import MonthlyStatements from "./pages/settlements/MonthlyStatements";
import AuditLogs from "./pages/admin/AuditLogs";

const P = (zh: string, en: string, cn?: string) => ({ "zh-TW": zh, "en-US": en, "zh-CN": cn ?? zh });

type Role = "admin" | "dispatcher" | "agency" | "fleet";

function Protected({ allow, children }: { allow: Role[]; children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user || !allow.includes(user.role)) return <NotFound />;
  return <>{children}</>;
}

function AppRoutes() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <Switch>
      <Route path="/" component={Dashboard} />

      {/* 已登入時別停在 /login / /register（直連、瀏覽器記錄、登入動作後） */}
      <Route path="/login">
        <Redirect to="/" />
      </Route>
      <Route path="/register">
        <Redirect to="/" />
      </Route>

      {/* 帳號設定（四角色；2026-08-21 帳號管理批次） */}
      <Route path="/account" component={AccountSettings} />

      {/* 訂單列表（所有角色，但內容依角色不同） */}
      <Route path="/orders">
        <OrdersList />
      </Route>

      {/* agency 建單 */}
      <Route path="/orders/new">
        <Protected allow={["agency"]}>
          <NewOrder />
        </Protected>
      </Route>

      {/* dispatcher 代客建單（人工報價）— 須在 /orders/:id 之前，避免被當成 :id */}
      <Route path="/orders/new-for-agency">
        <Protected allow={["dispatcher", "admin"]}>
          <NewOrderForAgency />
        </Protected>
      </Route>

      {/* 訂單詳情（所有角色，後端守門） */}
      <Route path="/orders/:id" component={OrderDetail} />

      {/* fleet 待確認派遣 */}
      <Route path="/dispatch-inbox">
        <Protected allow={["fleet"]}>
          <OrdersList
            variant="pendingForFleet"
            title={P("待確認派遣單", "Pending dispatches")}
            subtitle={P(
              "你被指派但尚未確認的訂單。請逐一確認接單；如無法承接請聯繫平台調度。",
              "Orders assigned to you awaiting confirmation. Contact platform dispatch if you cannot take one.",
            )}
          />
        </Protected>
      </Route>

      {/* dispatcher 報價作業 */}
      <Route path="/quoting">
        <Protected allow={["dispatcher", "admin"]}>
          <OrdersList
            variant="pendingQuote"
            title={P("待報價訂單", "Pending quotes")}
            subtitle={P(
              "需求方剛送來、或被退回的訂單。系統粗估已寫入，點進去手動調整最終報價。",
              "Newly submitted or rejected. System estimate available — adjust and submit.",
            )}
          />
        </Protected>
      </Route>

      {/* dispatcher 派遣作業 */}
      <Route path="/dispatch">
        <Protected allow={["dispatcher", "admin"]}>
          <OrdersList
            variant="pendingDispatch"
            title={P("待派遣訂單", "Pending dispatch")}
            subtitle={P(
              "需求方已確認報價的訂單。挑選車隊手動指派。",
              "Confirmed orders awaiting fleet assignment.",
            )}
          />
        </Protected>
      </Route>

      {/* 合作方管理 */}
      <Route path="/parties">
        <Protected allow={["dispatcher", "admin"]}>
          <Parties />
        </Protected>
      </Route>

      {/* 通知中心 */}
      <Route path="/notifications" component={Notifications} />

      {/* 帳務結算（Phase 7）：agency/fleet 每月訂單；dispatcher/admin 工作台 */}
      <Route path="/settlements">
        {user.role === "agency" || user.role === "fleet" ? <MonthlyStatements /> : <SettlementConsole />}
      </Route>
      <Route path="/payments">
        <Protected allow={["dispatcher", "admin"]}>
          <Placeholder
            title={P("金流管理", "Payments")}
            description={P("Airwallex 付款連結產生、Webhook 對帳、收款紀錄。客戶提供商戶帳號後接入。", "Airwallex hosted checkout & reconciliation — pending merchant credentials.")}
            phase="Phase 7"
          />
        </Protected>
      </Route>
      <Route path="/pricing">
        <Protected allow={["admin"]}>
          <PricingRules />
        </Protected>
      </Route>
      <Route path="/fleet-costs">
        <Protected allow={["admin", "dispatcher"]}>
          <FleetCosts />
        </Protected>
      </Route>

      {/* 基礎資料後台（20260611 反饋批次）— 平台共管：dispatcher＋admin */}
      <Route path="/vehicle-models">
        <Protected allow={["admin", "dispatcher"]}>
          <VehicleModels />
        </Protected>
      </Route>
      <Route path="/pois">
        <Protected allow={["admin", "dispatcher"]}>
          <Pois />
        </Protected>
      </Route>
      <Route path="/route-prices">
        <Protected allow={["admin", "dispatcher"]}>
          <RoutePrices />
        </Protected>
      </Route>
      <Route path="/charter-plans">
        <Protected allow={["admin", "dispatcher"]}>
          <CharterPlans />
        </Protected>
      </Route>
      <Route path="/exchange-rates">
        <Protected allow={["admin", "dispatcher"]}>
          <ExchangeRates />
        </Protected>
      </Route>
      <Route path="/commission-rules">
        <Protected allow={["admin", "dispatcher"]}>
          <CommissionRules />
        </Protected>
      </Route>
      {/* 帳號管理後台（admin；2026-08-21 帳號管理批次） */}
      <Route path="/users">
        <Protected allow={["admin", "dispatcher"]}>
          <Users />
        </Protected>
      </Route>
      {/* 常用司機名冊（供應方；2026-08-26） */}
      <Route path="/drivers">
        <Protected allow={["fleet"]}>
          <Drivers />
        </Protected>
      </Route>
      <Route path="/settings">
        <Protected allow={["admin"]}>
          <Placeholder
            title={P("系統參數", "System Settings")}
            description={P("結算週期、付款期限、預設貨幣（帳號管理已移至「帳號管理」頁）。", "Settlement cycle, payment deadline, default currency (accounts moved to the Accounts page).")}
            phase="Phase 7"
          />
        </Protected>
      </Route>
      <Route path="/audit-logs">
        <Protected allow={["admin"]}>
          <AuditLogs />
        </Protected>
      </Route>

      <Route component={NotFound} />
    </Switch>
  );
}

export default function App() {
  const { user, isLoading } = useAuth();
  const { t, setLang } = useLanguage();

  // 登入後以帳號設定的介面語言為準（/account 與頂欄切換都會同步寫回伺服器）
  const userId = user?.id ?? null;
  const userLanguage = user?.language ?? null;
  useEffect(() => {
    if (userId !== null && userLanguage) setLang(userLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-muted-foreground">
          {t({ "zh-TW": "載入中…", "en-US": "Loading…", "zh-CN": "载入中…" })}
        </p>
      </div>
    );
  }

  if (!user) {
    return (
      <Switch>
        <Route path="/login" component={Login} />
        <Route path="/register" component={Register} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route>
          <Redirect to="/login" />
        </Route>
      </Switch>
    );
  }

  // 管理員建立／重設密碼的帳號：改完密碼前不進工作台
  if (user.mustChangePassword === "yes") {
    return <ForcePasswordChange />;
  }

  return (
    <AppShell>
      <AppRoutes />
    </AppShell>
  );
}
